<template>
    <div class="expected-ebitda">
        <div class="expected-ebitda-box">
            <div class="period">
                Forventet EBITDA i indeværende regnskabsår
            </div>
            <div class="inside">
                <div>
                    <div class="small-title">Forventet EBITDA (driftsoverskud)</div>
                    <div class="no-budget" v-if="data && !data.forecast">
                        Hvis du lægger et budget i proEBITDA, vil du her kunne se, hvad regnskabsårets EBITDA med stor sandsynlighed vil blive. Kontakt os, hvis du har spørgsmål.
                    </div>
                    <div class="large-title color-good" v-else>
                        <span v-if="data">{{ data.expected_ebitda | currency }}</span>
                        <span v-else-if="error">-</span>
                        <span v-else><i class="fas fa-spinner fa-spin" /></span>
                    </div>
                </div>
                <div class="cta" @click="$modal.show('expected-ebitda')" v-if="data && data.forecast">
                    Læs mere
                </div>
            </div>
        </div>

        <modal name="expected-ebitda" height="auto" :adaptive="true" :scrollable="true" width="900">
            <div class="expected-ebitda-modal" v-if="data && data.forecast">
                <div class="modal-close" @click="$modal.hide('expected-ebitda')">
                    <i class="fal fa-times" />
                </div>
                <h3>Sådan er tallet beregnet</h3>

                <div class="sub">
                    Regnskabsåret {{ data.accounting_year.from | moment("D. MMM YYYY") }} - {{ data.accounting_year.to | moment("D. MMM YYYY") }}. Hold musen over <i class="fas fa-info-circle" /> for at se mellemregningerne.
                </div>

                <table class="calc">
                    <tr>
                        <td>
                            Realiseret EBITDA
                            <calc-tooltip title="Bogført og kassekladder" :lines="realizedLines" />
                        </td>
                        <td class="formula">Bogført og kassekladder, regnskabsårets start til i dag</td>
                        <td class="amount">{{ data.realized_ebitda | currency }}</td>
                    </tr>
                    <tr>
                        <td>
                            Værdi af opgaver
                            <calc-tooltip title="Opgaver der ikke er faktureret færdig" :lines="wipLines" />
                        </td>
                        <td class="formula">{{ data.wip.ongoing_count + data.wip.unbilled_count }} opgaver</td>
                        <td class="amount">{{ data.wip.total | currency }}</td>
                    </tr>
                    <tr>
                        <td>
                            Forventet resten af regnskabsåret
                            <calc-tooltip title="Kundetimer × DB pr. time over nulpunktsomsætning" :lines="forecastLines" />
                        </td>
                        <td class="formula">{{ data.forecast.remaining_hours | number }} kundetimer × {{ Math.abs(data.forecast.per_hour_above_break_even) | currency }} {{ data.forecast.per_hour_above_break_even < 0 ? 'under' : 'over' }} nulpunktsomsætning</td>
                        <td class="amount">{{ data.forecast.amount | currency }}</td>
                    </tr>
                    <tr class="sum">
                        <td>Forventet EBITDA</td>
                        <td class="formula">Realiseret + opgaver + resten af regnskabsåret</td>
                        <td class="amount">{{ data.expected_ebitda | currency }}</td>
                    </tr>
                </table>

                <div class="budget-note" :class="aboveBudget ? 'is-good' : 'is-bad'">
                    <template v-if="cphDeviationPercent !== null">
                        Du ligger <b>{{ cphDeviationPercent }} % {{ cphDeviation < 0 ? 'under' : 'over' }}</b> dit budget på DB pr. time.
                    </template>
                    Fortsætter du sådan, tjener du <b>{{ Math.abs(data.forecast.budget_deviation) | currency }} {{ aboveBudget ? 'mere' : 'mindre' }}</b> end budgetteret resten af regnskabsåret.
                    <calc-tooltip title="Sammenholdt med budgettet" :lines="comparisonLines" />
                </div>

                <ebitda-development class="development" :months="data.months" :wip="data.wip" />
            </div>
        </modal>
    </div>
</template>

<style lang="scss" scoped>
@import "../../../styles/vars.scss";

    .expected-ebitda {
        height: 100%;

        .expected-ebitda-box {
            background: $color--dark;
            color: #fff;
            border-radius: $borderradius--main;
            box-shadow: $boxshadow--creative;
            overflow: hidden;
            text-align: center;
            height: 100%;
            display: flex;
            flex-direction: column;

            .inside {
                padding: $padding--main;
                flex: 1;
                display: flex;
                align-items: center;
                justify-content: space-between;
                text-align: left;
            }

            .period {
                background: rgba(255, 255, 255, 0.05);
                padding: 10px;
                font-weight: bold;
                font-family: $fontfamily--main;

                span {
                    font-weight: normal;
                    opacity: 0.7;
                    margin-left: 5px;
                }
            }

            .small-title {
                margin-bottom: 5px;
            }

            .large-title {
                font-size: $fontsize--main * 1.6;
                font-weight: $fontweight--bold;
                font-family: $fontfamily--main;
            }

            .no-budget {
                font-size: $fontsize--main * 0.9;
                line-height: 1.5;
                opacity: 0.85;
            }

            .cta {
                flex-shrink: 0;
                margin-left: 15px;
                padding: 4px 12px;
                border: 1px solid rgba(255, 255, 255, 0.4);
                border-radius: $borderradius--main;
                font-size: $fontsize--main * 0.85;
                cursor: pointer;

                &:hover {
                    background: rgba(255, 255, 255, 0.1);
                }
            }
        }
    }

    .expected-ebitda-modal {
        padding: $padding--main;
        position: relative;

        .modal-close {
            position: absolute;
            top: 15px;
            right: 20px;
            font-size: 22px;
            cursor: pointer;
        }

        h3 {
            font-family: $fontfamily--main;
            font-weight: $fontweight--bold;
            margin: 0 0 5px;
        }

        .sub {
            font-size: $fontsize--main * 0.85;
            color: #777;
            margin-bottom: 15px;
        }

        .development {
            margin-top: 30px;
        }

        .budget-note {
            margin-top: 20px;
            padding: 12px 16px;
            background: #f5f6f8;
            border-left: 4px solid transparent;
            border-radius: $borderradius--main;
            font-size: $fontsize--main * 0.9;
            line-height: 1.6;

            &.is-good {
                background: rgba($color--good, 0.1);
                border-left-color: $color--good;
            }

            &.is-bad {
                background: rgba($color--bad, 0.1);
                border-left-color: $color--bad;
            }
        }

        table.calc {
            width: 100%;
            border-collapse: collapse;
            font-size: $fontsize--main * 0.9;

            td {
                padding: 6px 8px;
                vertical-align: top;
            }

            td {
                border-top: 1px solid #eee;
                padding: 12px 8px;
                font-weight: $fontweight--bold;
                font-family: $fontfamily--main;
            }

            tr.sum td {
                border-top: 2px solid $color--dark;
                padding-top: 10px;
                font-weight: $fontweight--bold;
                font-family: $fontfamily--main;
            }

            .formula {
                color: #888;
                font-size: $fontsize--main * 0.8;
            }

            .amount {
                text-align: right;
                white-space: nowrap;
            }
        }
    }
</style>

<script>
import * as calculationService from '@/services/calculationService'
import calcTooltip from './calcTooltip.vue'
import EbitdaDevelopment from '@/components/elements/EbitdaDevelopment.vue'

export default {
    components: { calcTooltip, EbitdaDevelopment },
    data () {
        return {
            data: null,
            error: false
        }
    },
    computed: {
        realizedLines () {
            const from = this.$options.filters.moment(this.data.accounting_year.from, 'D. MMM YYYY')
            const today = this.$options.filters.moment(new Date(), 'D. MMM YYYY')
            return [
                { label: 'Bogført', formula: from + ' - ' + today, amount: this.data.realized.booked },
                { label: 'Kassekladder', formula: 'Endnu ikke bogført', amount: this.data.realized.journal },
                { label: 'Realiseret EBITDA', formula: '', amount: this.data.realized_ebitda, total: true }
            ]
        },
        wipLines () {
            return [
                { label: 'Igangværende opgaver', formula: this.data.wip.ongoing_count + ' opgaver', amount: this.data.wip.ongoing },
                { label: 'Afsluttede, ikke fakturerede', formula: this.data.wip.unbilled_count + ' opgaver', amount: this.data.wip.unbilled },
                { label: 'Værdi i alt', formula: '', amount: this.data.wip.total, total: true }
            ]
        },
        forecastLines () {
            return this.data.forecast.lines
        },
        comparisonLines () {
            return this.data.forecast.comparison
        },
        cphDeviation () {
            return this.data.forecast.coverage_per_hour - this.data.forecast.budget.cph_calculated
        },
        cphDeviationPercent () {
            const budgeted = this.data.forecast.budget.cph_calculated
            if (!(budgeted > 0)) {
                return null
            }
            return (Math.abs(this.cphDeviation) / budgeted * 100).toFixed(1).replace('.', ',')
        },
        aboveBudget () {
            return this.data.forecast.budget_deviation >= 0
        }
    },
    methods: {
        getData () {
            this.data = null
            this.error = false
            calculationService.find('expected-ebitda', {}, (response) => {
                this.data = response.data
            }, (err) => {
                this.error = true
                console.log(err)
            })
        }
    },
    created () {
        this.getData()
    }
}
</script>



// WEBPACK FOOTER //
// expectedEbitda.vue?2c0af2ca